import { PageShell } from "../../components/PageShell";
import { useStaleBuildGuard } from "../../features/release/useStaleBuildGuard";
import { StepSkeleton } from "./StepSkeleton";
import { FlowShell, FlowShellProvider, requestStepFocus } from "../../components/PageShell";
import { SignOutContext } from "../../components/SessionRow";
import { useBookSignOut } from "../../auth/useBookSignOut";
import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useApolloClient } from "@apollo/client/react";
import { Navigate, useLocation, useNavigate, useNavigationType, useParams } from "react-router-dom";
import { isPossibleSlug } from "../../lib/slug";
import { useBookingProfile } from "../../features/profile/useBookingProfile";
import { checkPolicy, type PolicyCheck } from "../../features/profile/policy";
import {
  flowRecordVersion,
  isDroppedTime,
  noteIdentity,
  recordTime,
  subscribeFlowRecord,
  type FlowIdentity,
} from "../../features/booking/flowRecord";
import { useFlowState } from "../../features/booking/useFlowState";
import { useHold } from "../../features/booking/useHold";
import { useBooking } from "../../features/booking/useBooking";
import { PAYMENTS_ENABLED } from "../../config/payments";
import { useTranslation } from "../../i18n/useTranslation";
import NotFound from "../NotFound";
import StepService, { ServiceStepSkeleton } from "./StepService";
import StepTime from "./StepTime";
import StepDetails, { type BookingDetails, type RequiredField } from "./StepDetails";
import StepReview from "./StepReview";
import { recoverableSlotRefusal, reviewStateFor } from "./reviewState";
import BookingOutcome from "./BookingOutcome";
import { BookingUnauthorized } from "./BookingUnauthorized";
import { useIdentity } from "../../auth/IdentityProvider";
import { useHoldSessionEnd } from "../../auth/sessionEndGuard";
import { useGuestCheckoutPort } from "../../features/guestCheckout/portContext";
import BookSignIn from "./BookSignIn";
import { accountCreateChunk, payScreensChunk, WithStepChunk } from "./lazySteps";
import { PayChunkFallback } from "./PayChunkFallback";
import { checkoutBlockFor } from "../../features/payments/checkoutStart";
import { CHECKOUT_RETURN_PATH, bootCheckoutReturn } from "../../features/payments/returnBoot";
import { SUPPORT_HREF } from "../../lib/support";
import { useTermsConsent } from "../../features/terms/useTermsConsent";
import { readBookingTerms } from "../../features/terms/operations";
import { termsLocaleFor, type TermsLocale } from "../../features/terms/terms";
import { TermsDocumentContext } from "../../features/terms/TermsDocumentContext";
import { retryApiRead } from "../../../../../shared/lib/appCheckRecovery";
import { LoadFailed } from "../../components/LoadFailed";
import GuestFlowHost from "./guest/GuestFlowHost";
import { reportOutcome } from "../../analytics/session";
import { useRotateAfterOutcome } from "../../auth/guestRotation";
import { CLAIM_CEILING_MS, useGuestIdentityClaim } from "../../lib/guestSession";
import { CHECKOUT_EXPIRED_REPLAY_IS_DEFINITE } from "../../features/booking/replayDefinite";

// All of these are nested under `/:slug/book/:step`, so none costs a name out
// of the root slug space.
//
export const STEPS = ["signin", "create", "service", "date", "time", "details", "review"] as const;
export type Step = (typeof STEPS)[number];

/** Only these two can fail, so this is the whole error space. */
const REQUIRED: readonly RequiredField[] = ["name", "email"];

// book.uselura.com/<slug>/book/<step>
//
// Nested under the slug, so it costs NOTHING from the root slug space — the
// thing every top-level path spends. See docs/architecture/routes-book.md.
//
// This layout owns three things the steps must not each re-derive:
//
//  1. The professional. Every step needs the profile, and resolving it per step
//     would mean three round trips through the flow and a flash of loading
//     between each one.
//  2. The GUARD RAILS. A step is only reachable once its prerequisites exist —
//     you cannot pick a time before a day. Enforced by redirect rather than by
//     hiding the link, because the URL is the navigation here and someone will
//     paste one.
//  3. Account gating. Product requires it BEFORE slot selection, so it lives at
//     the entrance to the flow rather than at commit.

function Frame({ children }: { children: React.ReactNode }) {
  // `.b-scroll` inside, not bare children. `.b-app` has no padding of its own —
  // every real screen gets its gutter from the scroll region — so a loading or
  // error message dropped straight into it sat flush against the bezel. It is
  // also what the desktop layer keys on to give this the same measured column
  // as everything else, rather than full-bleed text at 1280px.
  return (
    <PageShell>{children}</PageShell>
  );
}

/**
 * The signed-in client's account could not be confirmed (refused, unknown, or
 * the read failed). Not creation authority, so no form and no booking: the
 * support route, and Try again.
 *
 * Try again RE-RESOLVES THE ACCOUNT IN PLACE (IdentityProvider retryAccount:
 * resolveClientAccount for the same user, the same network-only reads a
 * reload would run), through retryApiRead, which reloads instead only when App
 * Check is why the read failed. Until c3 it always reloaded, which also
 * dropped the flow's in-memory state.
 */
function AccountUnavailable({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  // The visible heading, the same approved string that titles /book/create
  // for a signed-in client (c3; it was the one main-route state left on "Lura").
  usePageTitle(t("flow.create.heading.existing"));
  return <>
    <h1 className="b-h2">{t("flow.create.heading.existing")}</h1>
    <div className="b-notice t-danger" role="alert">
      <p>{t("flow.create.error.setup")}</p>
      <a href={SUPPORT_HREF}>{t("flow.create.error.support")}</a>
    </div>
    <button type="button" className="b-btn v-secondary" onClick={() => retryApiRead(onRetry)} data-testid="account-unavailable-retry">
      {t("profile.error.retry")}
    </button>
  </>;
}

/**
 * Which form field the server rejected.
 *
 * The contract names the offending path in `invalidArgs`, e.g.
 * `input.guestContact.email`. Guest-contact validation is the ONLY stage that
 * carries a `detailCode`; reading that field off any other stage yields
 * undefined, which is why the classifier only sets it here.
 *
 * `firstName` / `lastName` are in the contract's code list and we send neither
 * — the API took a single `fullName` on 2026-08-27 — but they are mapped to the
 * name field anyway so a server still describing the old shape lands somewhere
 * a client can act on, rather than nowhere.
 */
function contactFieldFrom(path: string | null): RequiredField | null {
  if (!path) return null;
  if (path.includes("email")) return "email";
  if (path.includes("fullName") || path.includes("firstName") || path.includes("lastName")) {
    return "name";
  }
  return null;
}

/** A query string without its `time` (see `guardSearch` in Book). */
function searchWithoutTime(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("time");
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

function Book({ step }: { step: Step }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { account, status, user: identityUser, retryAccount } = useIdentity();

  // The client's own details live in MEMORY, not the URL.
  //
  // Everything else about the flow is in the query string so that back, forward
  // and refresh behave the way a phone user expects. A name and an email are
  // the exception: a booking link gets shared, and a shared link must not carry
  // somebody's contact details.
  const [details, setDetails] = useState<BookingDetails>({});
  const [invalid, setInvalid] = useState<readonly RequiredField[]>([]);

  // THE HOLD BELONGS TO THE FLOW, not to the time step.
  //
  // Its id is what `createBooking` consumes, so a hold scoped to StepTime could
  // not reach the screen that commits. It is also flow-scoped in behaviour: the
  // countdown keeps running while the client types their name, and a hold that
  // expires on Review has to be handled there.
  const hold = useHold();
  const booking = useBooking();
  // A port injected by a test or the harness, else null: GuestFlowHost then
  // builds the booking-v1 adapter (features/guestCheckout/portContext.tsx).
  // EVERY anonymous booking takes the verified guest checkout. There is no
  // other anonymous path: the unverified legacy commit was removed with the
  // guest-verification OFF build (f13, 2026-10-07).
  const guestPort = useGuestCheckoutPort();
  /*
   * THE OUTCOME EVENT IS PRODUCED HERE BECAUSE THE OUTCOME LIVES HERE.
   *
   * `booking.state` is the server's answer, already refused by
   * `isAuthorizedPilotOutcome` if it is not one this cohort can describe.
   * Every other place that could produce this event would be inferring it —
   * from which screen rendered, from the step reached, or from an HTTP status
   * — and `analytics/outcome.ts` exists to refuse that.
   *
   * A no-op in every committed configuration: `reportOutcome` goes through the
   * same six gates as everything else, and an unmapped state sends nothing.
   * Deduped on the outcome VALUE, so the recovery check re-entering
   * `uncertain` does not resend.
   */
  useEffect(() => {
    reportOutcome(booking.state);
  }, [booking.state]);
  // A SESSION END WAITS WHILE THE ANSWER IS ON SCREEN (auth/sessionEndGuard).
  // useBooking holds it while sending and while the outcome is unknown; here,
  // while Review shows a booking that exists (or an answer that may describe
  // one). Signing out now would replace the outcome with sign-in, and someone
  // who never saw "You're booked" books again. Leaving Review releases it.
  useHoldSessionEnd(
    step === "review" &&
      (booking.state.status === "done" || booking.state.status === "pay" || booking.state.status === "unauthorized")
  );

  // THE SERVER REJECTED THE NAME OR EMAIL, so the screen that can fix it is
  // Details — not a generic retry on Review, which asks the client to press the
  // same button on the same unchanged values and get the same refusal.
  //
  // The draft survives because `details` lives here, in the flow, rather than
  // on the step. Flagging the field drives StepDetails' existing accessible
  // error and its focus handling, so a keyboard or screen-reader client lands
  // ON the field being complained about.
  //
  // `booking.reset()` clears the failure as we leave, so returning to Review
  // later does not re-trigger this and bounce them back.
  const contactRejection =
    booking.state.status === "failed" && booking.state.failure.kind === "contact-invalid"
      ? booking.state.failure
      : null;
  const rejectedField = contactFieldFrom(contactRejection?.field ?? null);
  // A NEW IDENTITY PER REJECTION. StepDetails mounts fresh on this path, so it
  // cannot tell a server refusal from a restored tab on its own — and moving
  // focus for a restored tab would throw up a phone keyboard nobody asked for.
  // Counting means a second refusal of the SAME field still moves focus.
  const [rejectionToken, setRejectionToken] = useState<string | null>(null);
  useEffect(() => {
    if (!contactRejection || !slug) return;
    setRejectionToken((current) => `${(Number(current?.split(":")[0] ?? 0) + 1)}:${rejectedField ?? "form"}`);
    setInvalid(rejectedField ? [rejectedField] : ["name", "email"]);
    booking.reset();
    navigate(`/${slug}/book/details${window.location.search}`, { replace: true });
    // `booking` is intentionally excluded — `reset` is stable and including the
    // whole object would re-run this on every state change it causes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactRejection, rejectedField, navigate, slug]);

  /*
   * A SLOT-LEVEL REFUSAL BELONGS TO THE HOLD IT WAS ABOUT.
   *
   * `reviewStateFor` lets a commit failure outrank the hold, which is right
   * while that hold is the one on screen. But nothing ever cleared it, so once
   * a commit came back hold-gone (or slot-unavailable) every LATER hold still
   * rendered "Your held time ran out" with Confirm disabled — the client could
   * not book again without reloading. Measured on the account path's OFF-build
   * twin, for the same time and for a different one (Web Director A1,
   * 2026-09-26).
   *
   * Two moves:
   * - hold-gone means the server has already let that hold go, but its clock
   *   has not run out here, so useHold still says `held` and Time ignores a tap
   *   on that slot. Drop it, so choosing the same time asks for a new hold.
   * - a hold that LEFT `held` after the refusal (dropped, expired, or replaced
   *   — acquiring passes through `acquiring`) and then came back is a new
   *   attempt: the refusal no longer describes anything on screen, so it is
   *   cleared. Nothing was booked (the server refused definitively), so there
   *   is no attempt to keep. Keyed on that gap rather than on the hold id,
   *   because Backend may answer the same owner and window with the same id.
   *
   * Slot-unavailable keeps its dead-end while the SAME hold stays held:
   * someone else has that time, and Time ignores choosing it again.
   *
   * After a Check (`afterCheck`) too: the first send may, rarely, have
   * committed, so useBooking.commit asks under the OLD key before minting a
   * new one (Web Director A2). The recovery is safe because of that, not
   * because the refusal is assumed to mean nothing was booked.
   */
  const heldId = hold.state.status === "held" ? hold.state.hold.id : null;
  const failedKind = recoverableSlotRefusal(booking.state);
  const slotRefusal = failedKind !== null;
  const refusalPhase = useRef<"idle" | "refused" | "gap">("idle");
  // The hold the refusal was about. A different id is a new hold even if no
  // render ever showed a gap between the two (review A2).
  const refusedHoldId = useRef<string | null>(null);
  const { release: releaseHold, forget: forgetHold } = hold;
  /*
   * A HOLD BELONGS TO THE UID THAT REQUESTED IT (security audit L1).
   * If the current user is no longer that uid, the hold is not ours to commit:
   * forget it locally (a release would be refused under the new token) so
   * Review shows no hold and Confirm cannot send it. A same-uid link keeps it.
   */
  const currentUid = identityUser?.uid ?? null;
  const heldOwner = hold.state.status === "held" ? hold.state.ownerUid ?? null : null;
  useEffect(() => {
    if (heldOwner && currentUid && heldOwner !== currentUid) forgetHold();
  }, [heldOwner, currentUid, forgetHold]);
  const { reset: resetBooking, forgetFor: forgetBookingFor } = booking;
  // The same rule for a booking attempt (rc.11 1c): another uid now, so the
  // previous person's attempt, its re-ask and its outcome leave the page.
  useEffect(() => {
    if (currentUid) forgetBookingFor(currentUid);
  }, [currentUid, forgetBookingFor]);
  useEffect(() => {
    if (!slotRefusal) {
      refusalPhase.current = "idle";
      return;
    }
    if (refusalPhase.current === "idle") {
      refusalPhase.current = heldId ? "refused" : "gap";
      refusedHoldId.current = heldId;
      if (failedKind === "hold-gone" && heldId) void releaseHold();
      return;
    }
    if (!heldId) {
      refusalPhase.current = "gap";
      return;
    }
    if (refusalPhase.current === "gap" || heldId !== refusedHoldId.current) {
      refusalPhase.current = "idle";
      resetBooking();
    }
  }, [slotRefusal, failedKind, heldId, releaseHold, resetBooking]);

  // The booking that came back was an EARLIER attempt's (useBooking
  // `resolvedPrior`); the hold taken since was never consumed. Free it rather
  // than leave the slot blocked for others until it expires (review A2).
  // A "pay" for an earlier attempt's time (the hold rule, 2026-10-20) too: the
  // gate first, so a closed build folds it away.
  const resolvedPrior =
    (booking.state.status === "done" || (PAYMENTS_ENABLED && booking.state.status === "pay")) && booking.state.resolvedPrior === true;
  useEffect(() => {
    if (resolvedPrior && heldId) void releaseHold();
  }, [resolvedPrior, heldId, releaseHold]);

  /*
   * RETIRE THE ANONYMOUS IDENTITY ONCE A DEFINITE OUTCOME IS ON SCREEN
   * (security audit M1; auth/guestRotation.ts).
   *
   * On a shared device the next person to book, sign in or create an account
   * would otherwise link THEIR account to this guest's UID and inherit this
   * booking. The guest keeps it through the management link, a bearer
   * capability that needs no session.
   *
   * Only CONFIRMED or REQUESTED, and only while that screen is rendered: the
   * guest checkout's host reports it (`onOutcomeShown`). Never an unknown
   * outcome: the guest checkout claims the identity until the answer is
   * known, so a same-UID Check always remains possible. A signed-in account
   * books through `useBooking` and is never signed out;
   * `rotateGuestIdentity` refuses anything but the anonymous UID named here.
   * An unconsumed hold's release (`resolvedPrior`) lands first (`settled`).
   * Refused because another tab (or this one) still claims the identity, the
   * retirement is not dropped: it becomes pending and happens as soon as
   * nothing claims it (security audit F1).
   */
  const [outcomeUid, setOutcomeUid] = useState<string | null>(null);
  useRotateAfterOutcome(outcomeUid, hold.settled);
  // The verified guest checkout's own "a booking may be in flight or may
  // exist" (GuestFlowHost `onUnresolved`). useBooking never sees a guest's
  // commit, so without this the identity-change gates below would read a
  // guest's sending or unknown booking as safe (reviewer P2, rc.2).
  const [guestUnresolved, setGuestUnresolved] = useState(false);
  // F5(c)1: the professional whose in-flight checkout this page has already
  // dealt with (the block lifted, or decided again), so it is not asked twice.
  const [checkoutSettledFor, setCheckoutSettledFor] = useState<string | null>(null);
  const [checkoutRecheck, setCheckoutRecheck] = useState(0);
  /*
   * A LIVE HOLD IS OWNED BY THE UID, so it claims the identity against idle
   * rotation. Not once a definite outcome is showing: the booking consumed
   * that hold, and it must not keep the identity it booked under alive.
   */
  const holdLive = hold.state.status === "acquiring" || hold.state.status === "held";
  // And only until the server's expiry (F1): a throttled background tab whose
  // countdown has stalled must not keep claiming a hold that no longer exists.
  // Capped at the hold's documented ten minutes, and an unreadable expiry is
  // no claim (N4); an acquisition in flight gets a short fixed bound.
  const holdUntil = hold.state.status === "held" ? Date.parse(hold.state.hold.expiresAt) : Number.POSITIVE_INFINITY;
  useGuestIdentityClaim(
    holdLive && outcomeUid === null,
    holdUntil,
    hold.state.status === "held" ? CLAIM_CEILING_MS.hold : CLAIM_CEILING_MS.acquiring
  );


  // OFFLINE IS THE ONE STATE THE SERVER CANNOT REPORT, so it is read from the
  // browser. The review screen's copy for it is specific and deliberate — the
  // booking is not sent AND not queued, so it will not go through on its own —
  // and that claim is only true if we actually know we are offline.
  //
  // Subscribed rather than read once: a client who loses signal while typing
  // their details arrives at review with a stale answer otherwise.
  const [onlineNow, setOnlineNow] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const update = () => setOnlineNow(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  const { locale } = useTranslation();

  /*
   * LURA'S TERMS OF SERVICE, read on Review for the page's language. Read
   * only on Review: no other step shows or sends them. Book stays mounted
   * across steps, so the answer survives into the outcome screen that
   * replaces Review, whose footer then links the same document.
   *
   * This one is the signed-in Review's. The verified guest route reads its
   * own in GuestFlowHost, beside the checkout whose
   * refusals it must see in the same render; the same `readTerms` and scope.
   */
  const apollo = useApolloClient();
  const readTerms = useCallback((termsLocale: TermsLocale) => readBookingTerms(apollo, termsLocale), [apollo]);
  // `scope`: an agreement is for THIS professional and THIS hold. Leaving
  // Review, a new hold or another slug clears the tick (audit WEB-TOS-01).
  const termsScope = `${slug ?? ""}|${heldId ?? ""}`;
  // Backend refused the acceptance the last Confirm carried. The hook unticks
  // in the same render, then re-reads (stale, required) or shows frame 4.5
  // (unavailable). A READ, never a resend: the person agrees again and
  // presses Confirm themselves.
  const termsFailure =
    booking.state.status === "failed" && booking.state.failure.kind === "terms" ? booking.state.failure : null;
  const terms = useTermsConsent({
    read: readTerms,
    locale: termsLocaleFor(locale),
    enabled: step === "review" && account !== null,
    scope: termsScope,
    refusal: termsFailure,
  });

  const state = useBookingProfile(isPossibleSlug(slug) ? slug : undefined, locale === "es" ? "ES" : "EN");
  /*
   * rc.10: WHY THE LAST SUBMIT STOPPED BEFORE SENDING, if the policy re-read
   * stopped it: the policy changed (Review now shows the new one; a fresh
   * press is required), or it could not be read (the press is the retry).
   * Scoped like the Terms (slug and hold), so it never follows the client to
   * another time.
   */
  const [policyStop, setPolicyStop] = useState<{ check: Exclude<PolicyCheck, "same">; scope: string } | null>(null);

  /*
   * THE IDENTITY THE FLOW RECORD NOTES (rc.9 F3; features/booking/flowRecord.ts).
   * Resolved only once Firebase has answered: IdentityProvider subscribes
   * after first paint, so the first renders are "nobody, loading", and
   * reading them would drop a signed-in client's own time on every refresh
   * (security audit F3D-1). Undefined while loading; the steps show a
   * skeleton then.
   */
  const identityUid = identityUser?.uid ?? null;
  const identityCredentialed = identityUser ? !identityUser.isAnonymous : false;
  const identityResolved = identityUser !== null || status !== "loading";
  const flowIdentity: FlowIdentity | undefined = !identityResolved
    ? undefined
    : identityUid === null
      ? null
      : { uid: identityUid, credentialed: identityCredentialed };
  const recordVersion = useSyncExternalStore(subscribeFlowRecord, flowRecordVersion);
  const navigationType = useNavigationType();
  // A previous client's time, reached by Back (or the first load after their
  // sign-out reload), is never shown, not even for one frame (rc.9 F4). The
  // record's version is read here so this check follows every change to it.
  const flow = useFlowState({
    hideTime: (time) => recordVersion >= 0 && isDroppedTime(time, flowIdentity, navigationType === "POP"),
  });
  // THE QUERY A GUARD REDIRECT CARRIES: the URL's, minus a time this render
  // hid (a previous client's, reached by Back; rc.9 F4). Copying the raw
  // query would carry that time into a REPLACE, which the Back-only hide
  // does not cover (security audit WS9-1).
  const guardSearch = flow.timeHidden ? searchWithoutTime(window.location.search) : window.location.search;

  /*
   * SIGN OUT, from the footer's session row (components/SessionRow.tsx). An
   * UNFINISHED booking's temporary hold is released first; a completed
   * booking has none to release, and signing out never cancels or releases
   * a confirmed appointment or a submitted request (Product, 2026-10-07).
   */
  // A newer release found while a signed-in booking may exist must not offer
  // a reload over it: sending, uncertain, unauthorized, a Check's Terms
  // refusal, or the outcome on screen (rc.9 item 2; review P2). The same
  // states as the session row's `bookingUnresolved`, plus done.
  useStaleBuildGuard(
    booking.state.status === "sending" ||
      booking.state.status === "uncertain" ||
      booking.state.status === "unauthorized" ||
      booking.state.status === "done" ||
      booking.state.status === "pay" ||
      (booking.state.status === "failed" && booking.state.afterCheck === true)
  );
  // A paid booking's payment step ("pay") consumed its hold as surely as "done".
  const holdUnfinished =
    booking.state.status !== "done" &&
    booking.state.status !== "pay" &&
    (hold.state.status === "held" || hold.state.status === "acquiring");
  const sessionRow = useBookSignOut({
    slug: slug ?? "",
    requiresAccount: state.status === "ready" && state.profile.requiresClientAccount,
    releaseHold: holdUnfinished ? hold.release : undefined,
  });
  const { leavingTo, arrived } = sessionRow;
  const pathname = useLocation().pathname;
  useEffect(() => {
    if (leavingTo !== null && pathname === leavingTo) arrived();
  }, [leavingTo, pathname, arrived]);

  /*
   * A DIFFERENT PERSON, A CLEAN FLOW (sign-out increment). The details, the
   * chosen time and a completed booking's outcome live in this component's
   * memory, which survives step changes. When a SIGNED-IN client's identity
   * gives way (their Sign out, a session end, switching accounts), they are
   * that client's and are dropped. A guest's anonymous identity changing is
   * not that: it is retired right after its booking, with the outcome still
   * on screen, and rotated when idle (guestRotation.ts) — neither may wipe
   * the screen. The first identity a page gets keeps what was typed, and
   * linking a credential to the guest keeps its uid, so neither clears.
   */
  const { update: updateFlow } = flow;
  const bookingDone = booking.state.status === "done";
  const chosenTime = flow.selection.startTime;
  // THE IDENTITY NOTE RUNS FIRST, the recording after it (effects run in
  // declaration order within a commit): a signed-in identity that arrives in
  // the same commit as a URL time is noted before that time is recorded for
  // it (focused review P1-1, rc.8). A give-way drops every time the client
  // chose in this tab, kept in sessionStorage so a reload, a remount or a
  // restored tab cannot bring them back (rc.9 F3, design v2).
  useEffect(() => {
    if (!identityResolved) return;
    const identity: FlowIdentity = identityUid === null ? null : { uid: identityUid, credentialed: identityCredentialed };
    if (noteIdentity(identity, chosenTime) !== "gave-way") return;
    setDetails({});
    setInvalid([]);
    updateFlow({ startTime: null }, { replace: true });
    // The previous person's outcome and receipt, in memory only. Never a
    // booking that may still be unresolved: its idempotency key and Check
    // stay (and a session end waits for it, auth/sessionEndGuard.ts).
    if (bookingDone) resetBooking();
  }, [identityResolved, identityUid, identityCredentialed, updateFlow, bookingDone, resetBooking, chosenTime]);
  useEffect(() => {
    if (identityResolved) recordTime(chosenTime);
  }, [identityResolved, identityUid, identityCredentialed, chosenTime]);

  // The URL still names a time this render hid (a previous client's, by
  // Back): take it out of the URL too. Only on history moves and first loads:
  // the next person choosing that same slot themselves is a push, and keeps it.
  const { timeHidden } = flow;
  useEffect(() => {
    if (timeHidden) updateFlow({ startTime: null }, { replace: true });
  }, [timeHidden, updateFlow]);

  if (!isPossibleSlug(slug)) return <NotFound />;
  // ON THE WAY OUT (QE-SO-1). Firebase has signed the client out, and this
  // render may still be the previous person's Review or Details, whose guards
  // would send the next person onward to Details or Time. Go where Sign out
  // was going, ahead of every guard.
  if (leavingTo !== null && status !== "account" && pathname !== leavingTo) {
    return <Navigate to={leavingTo} replace />;
  }

  /*
   * ONE FLOW SHELL AT ONE TREE POSITION (c6). Every flow branch below returns
   * the same outer shape, the Terms context, then the guest host (or nothing),
   * then FlowShell, so React keeps the shell, and its header with Appearance,
   * across the profile arriving, the identity resolving and the step changing.
   * Before the profile is known there is no guest host and no Terms document;
   * the shape is the same so the shell is not remounted when they arrive.
   */
  const beforeProfile = (node: ReactNode) => (
    <TermsDocumentContext.Provider value={null}>
      <SignOutContext.Provider value={null}>
        <FlowShellProvider>
          {null}
          <FlowShell>{node}</FlowShell>
        </FlowShellProvider>
      </SignOutContext.Provider>
    </TermsDocumentContext.Provider>
  );

  // THE SERVICE STEP'S OWN SHAPE while it waits (StepService
  // ServiceStepSkeleton), the same placeholder `/:slug` shows before it
  // redirects here, so the hand-over does not change layout. Other steps keep
  // the plain frame: a service-shaped placeholder would predict the wrong page.
  if (state.status === "loading" && step === "service") {
    return beforeProfile(<ServiceStepSkeleton testId="book-loading" />);
  }
  if (state.status === "loading") {
    // Every content wait a person can see is a shape, not text (D4): the
    // generic step skeleton on the other step routes.
    return beforeProfile(<StepSkeleton testId="book-loading" />);
  }

  // A renamed link that lands mid-flow still has to move to the canonical name,
  // and it has to KEEP the selection — dropping the query string here would
  // silently restart someone three steps in.
  if (state.status === "moved") {
    return <Navigate to={`/${state.canonicalSlug}/book/${step}${guardSearch}`} replace />;
  }

  // Nothing to book: send them to the profile, which owns the copy for both of
  // these and says the right thing about each.
  if (state.status === "unavailable" || state.status === "not-bookable") {
    return <Navigate to={`/${slug}`} replace />;
  }

  if (state.status === "error") {
    // Inside the same Frame as the loading state above, so the header and
    // legal line stay mounted; LoadFailed moves focus to its heading. The
    // same visible heading already titles /:slug's load failure.
    return (
      <Frame>
        <LoadFailed title="profile.error.title" body="profile.error.body" retry="profile.error.retry" onRetry={state.retry} testId="book-load-error" autoReload="book" />
      </Frame>
    );
  }

  const { profile, rereadPolicy } = state;
  const policyNote = policyStop && policyStop.scope === termsScope ? policyStop.check : null;
  const bookingName = account?.name ?? details.name ?? "";
  const bookingEmail = account?.email ?? details.email ?? "";

  // THE GUEST CHECKOUT'S HOST is mounted at this one position on EVERY step,
  // so its state and key survive leaving Review. It renders only on an
  // anonymous Review (`active`); otherwise it renders nothing. That includes
  // the sign-in and create screens and every redirect below (`withGuestHost`):
  // an unresolved checkout that unmounted there would lose its same-UID
  // Check, and its report would go stale (reviewer P2, rc.2).
  const guestHost = (
    <GuestFlowHost
      port={guestPort}
      hold={hold.state}
      dropHold={hold.release}
      // A RESOLVED guest, on a Review the guards below let through (a service,
      // a time, and the contact details that live only in memory): while the
      // identity loads, or while a guard is redirecting, the shell shows the
      // skeleton and nothing of the guest's review, not even for one frame
      // (c6-2; review P3).
      active={
        step === "review" && status === "guest" && !account &&
        Boolean(flow.selection.serviceId && flow.selection.startTime && bookingName && bookingEmail)
      }
      slug={slug}
      profile={profile}
      rereadPolicy={rereadPolicy}
      selection={flow.selection}
      details={{ name: bookingName, email: bookingEmail }}
      online={onlineNow}
      readTerms={readTerms}
      termsScope={termsScope}
      onOutcomeShown={setOutcomeUid}
      onUnresolved={setGuestUnresolved}
      onContactRejected={(path) => {
        // The same handling as the account path's contact refusal above:
        // flag the field, move focus to it, and return to Details. The
        // guest's verified proof and key stay in the checkout.
        const field = contactFieldFrom(path);
        setRejectionToken((current) => `${Number(current?.split(":")[0] ?? 0) + 1}:${field ?? "form"}`);
        setInvalid(field ? [field] : ["name", "email"]);
        if (slug) navigate(`/${slug}/book/details${window.location.search}`, { replace: true });
      }}
    />
  );

  /*
   * THE SESSION ROW is offered to a signed-in client with their account
   * read, and ABSENT (not disabled) while their booking is sending, its
   * result unknown or unreadable, or a Check left the Terms uncertain: the
   * states in which a booking may exist that is not on screen. After a
   * booking is done it is offered: a shared device is as likely to be left
   * on "Booked" as anywhere.
   */
  // "pay": the checkout is bound to this session (checkoutStatus answers only
  // it), so the session is not given up while the payment step is on screen.
  const bookingUnresolved =
    booking.state.status === "sending" ||
    booking.state.status === "uncertain" ||
    booking.state.status === "unauthorized" ||
    booking.state.status === "pay" ||
    (booking.state.status === "failed" && booking.state.afterCheck === true);
  const session =
    status === "account" && account !== null && !bookingUnresolved
      ? { email: account.email, signOut: sessionRow.signOut }
      : null;

  // The shell's chrome sits above BOTH the guest host and the shell (c6-2),
  // so the host's screens, rendered into the shell, publish to it.
  const withGuestHost = (node: ReactNode) => (
    <TermsDocumentContext.Provider value={terms.documentUrl}>
      <SignOutContext.Provider value={session}>
        <FlowShellProvider>
          {guestHost}
          {node}
        </FlowShellProvider>
      </SignOutContext.Provider>
    </TermsDocumentContext.Provider>
  );

  /**
   * A step-guard redirect between two flow screens, drawn INSIDE the shell
   * (c6-2): the destination's skeleton for the one commit before the
   * Navigate lands, so the header (and a focused Appearance) is never
   * remounted. A bare Navigate here replaced the shell with nothing.
   */
  const redirectInShell = (to: string, toService = false) =>
    withGuestHost(
      <FlowShell>
        {toService && !profile.requiresClientAccount ? <ServiceStepSkeleton testId="book-loading" /> : <StepSkeleton testId="book-loading" />}
        <Navigate to={to} replace />
      </FlowShell>
    );

  // A Firebase credential alone is not a client booking account. Until the
  // authenticated account.self read proves matching UID and contact, no hold
  // or commit route may render, even for a guest-accepting professional.
  if (status === "loading" && step === "service" && !profile.requiresClientAccount) {
    return withGuestHost(<FlowShell><ServiceStepSkeleton testId="book-loading" /></FlowShell>);
  }
  if (status === "loading") {
    return withGuestHost(<FlowShell><StepSkeleton testId="book-loading" /></FlowShell>);
  }
  if (status === "account-unavailable") {
    // A refused or unknown account state is not creation authority. This also
    // covers direct /book/create entry, refresh, and SPA back navigation.
    return withGuestHost(<Frame><AccountUnavailable onRetry={retryAccount} /></Frame>);
  }
  // No identity change while a booking may be in flight or may exist: the
  // sign-in screen, its entry on the service step, and account creation
  // share this rule.
  // A guest's booking is the verified checkout's, which reports its own.
  const bookingSafe = booking.state.status !== "sending" &&
    booking.state.status !== "uncertain" && booking.state.status !== "done" &&
    booking.state.status !== "pay" &&
    booking.state.status !== "unauthorized" && !guestUnresolved;
  if (status === "account-setup" && step !== "create") {
    return withGuestHost(<Navigate to={`/${slug}/book/create`} replace />);
  }
  // A GUEST WHOSE BOOKING IS UNRESOLVED DOES NOT REACH THE CREATE FORM (audit
  // L-1). Linking or replacing the UID here would move, or strand, a booking
  // that may exist under it. The sign-in screen's link is already withheld;
  // this covers a typed URL, a refresh and Back. It goes where the sign-in
  // gate keeps people: the sign-in screen, which says why and offers no
  // identity change. `account-setup` is not redirected (it would loop back
  // here): its identity is already credentialed, and AccountCreate itself
  // refuses the in-place resolution while the booking is unresolved.
  if (step === "create" && status === "guest" && !bookingSafe) {
    return withGuestHost(<Navigate to={`/${slug}/book/signin${guardSearch}`} replace />);
  }
  if (step === "create" && status !== "account") {
    // The `account-create` chunk (rc.12 (a)): prefetched by the sign-in step,
    // started at entry for a first URL here; a skeleton only while on its way.
    return withGuestHost(
      <WithStepChunk chunk={accountCreateChunk} fallback={<FlowShell><StepSkeleton testId="step-chunk-loading" /></FlowShell>}>
        {({ default: AccountCreate }) => <AccountCreate slug={slug} profile={profile} releaseHold={hold.release} bookingSafe={bookingSafe} />}
      </WithStepChunk>
    );
  }
  if (profile.requiresClientAccount && status !== "account" && step !== "signin") {
    return withGuestHost(<Navigate to={`/${slug}/book/signin`} replace />);
  }
  if (step === "signin" && status === "guest") {
    return withGuestHost(<BookSignIn slug={slug} profile={profile} releaseHold={hold.release}
      bookingSafe={bookingSafe} search={guardSearch} />);
  }
  if (step === "signin" || step === "create") return withGuestHost(<Navigate to={`/${slug}/book/service`} replace />);

  /*
   * A FRESH LOAD WHILE THIS PERSON'S CHECKOUT FOR THIS BUSINESS MAY STILL BE
   * OPEN (F5(c)1, the Dev window, 2026-10-29; the Web Director's ruling): the
   * browser's Back from Stripe without a bfcache restore (Firefox) reloaded
   * Review, the guards below sent the client to an EMPTY Details form, and the
   * block appeared only after they typed everything again. Now, before any
   * guard: this tab's own checkout goes to /checkout/return (its status,
   * Resume only as a press); a live marker without this tab's record shows the
   * block. Never the form. Only while nothing is in flight on this page.
   */
  const freshCheckout = PAYMENTS_ENABLED && checkoutSettledFor !== profile.professionalId && booking.state.status === "idle" && !guestUnresolved
    ? checkoutBlockFor(profile.professionalId)
    : null;
  if (freshCheckout?.blocked && freshCheckout.ref !== null) return withGuestHost(<FreshCheckoutReturn />);
  if (freshCheckout?.blocked && payScreensChunk) {
    return withGuestHost(
      <WithStepChunk key={checkoutRecheck} chunk={payScreensChunk} fallback={<FlowShell><StepSkeleton testId="step-chunk-loading" /></FlowShell>} failed={<FlowShell><PayChunkFallback /></FlowShell>}>
        {({ PayBlock }) => (
          <FlowShell>
            <PayBlock
              checkoutRef={null}
              identity={freshCheckout.identity}
              professionalId={profile.professionalId}
              onLift={() => setCheckoutSettledFor(profile.professionalId)}
              onRecheck={() => setCheckoutRecheck((n) => n + 1)}
              onClosed={hold.release}
            />
          </FlowShell>
        )}
      </WithStepChunk>
    );
  }

  // GUARD RAILS. Each step needs the one before it. Redirect rather than render
  // an empty screen — someone pasting `/book/time` with no service chosen is
  // not an error case, it is a link that went stale in a message.
  if (step !== "service" && !flow.selection.serviceId) {
    return redirectInShell(`/${slug}/book/service`, true);
  }
  // Old five-step links must continue to work. The date is now chosen on Time;
  // replace keeps Back from reopening an obsolete intermediary screen.
  if (step === "date") {
    return redirectInShell(`/${slug}/book/time${guardSearch}`);
  }
  // Details and review both need a chosen TIME, not merely a day. Someone
  // pasting `/book/review` with a half-finished query string has a stale link,
  // not an error — send them to the step that can move them forward.
  if ((step === "details" || step === "review") && !flow.selection.startTime) {
    return redirectInShell(`/${slug}/book/time${guardSearch}`);
  }
  // Review additionally needs what Details collects. Those live in memory
  // rather than the URL, so a refresh ON review legitimately loses them — which
  // is why this redirects rather than rendering a summary with blanks in it.
  if (step === "review" && !(bookingName && bookingEmail)) {
    return redirectInShell(`/${slug}/book/details${guardSearch}`);
  }

  const shared = { slug, profile, flow, verification: account === null };

  const renderStep = () => {
  switch (step) {
    case "service":
      return <FlowShell><StepService {...shared} signIn={status === "guest" && bookingSafe} /></FlowShell>;
    case "time":
      return <FlowShell><StepTime {...shared} hold={hold} unresolved={guestUnresolved} /></FlowShell>;
    case "details":
      return (
        <FlowShell>
        <StepDetails
          {...shared}
          values={details}
          account={account}
          invalid={invalid}
          serverRejected={rejectionToken}
          // THE COUNTDOWN PRODUCT REQUIRES ON THIS STEP, from the server's
          // `expiresAt` by way of `useHold` — never a timer started here.
          secondsLeft={hold.state.status === "held" ? hold.state.secondsLeft : null}
          holdExpiresAt={hold.state.status === "held" ? hold.state.hold.expiresAt : null}
          holdExpired={hold.state.status === "expired"}
          // An anonymous client always books through the verified checkout.
          verifiedGuest={account === null}
          onChange={(field, value) => {
            setDetails((current) => ({ ...current, [field]: value }));
            // Clear THIS field's rejection as it is typed, not only on the next
            // submit. A parent that recomputes only on submit strands the
            // client: a non-empty `invalid` disables Continue, and a disabled
            // default button also kills implicit submission — so they fix both
            // fields and have no remaining way forward, keyboard included.
            setInvalid((current) => current.filter((name) => name !== field));
          }}
          onContinue={() => {
            const missing = REQUIRED.filter((field) => !(field === "name" ? bookingName : bookingEmail).trim());
            setInvalid(missing);
            if (missing.length === 0) {
              requestStepFocus();
              navigate(`/${slug}/book/review${window.location.search}`);
            }
          }}
        />
        </FlowShell>
      );
    case "review": {
      // An anonymous guest books through email verification: the host above
      // renders their Review. Only a signed-in client takes the path below.
      // The shell stays mounted, empty: the host renders the guest's screens
      // into it (FlowShellPortal, c6-2).
      if (account === null) return <FlowShell />;

      // A SUCCESSFUL COMMIT REPLACES THE SCREEN rather than navigating.
      //
      // There is no `/book/confirmed` route on purpose: the appointment id is
      // not in the URL, so such a route would be reachable by paste and by
      // refresh with nothing to show. The confirmation is a consequence of the
      // commit, not a place — and the durable way back to this appointment is
      // the management link in the email, which is the whole point of /a/*.
      if (booking.state.status === "done") {
        // BOTH outcomes land here, and the screen tells them apart. A manual
        // professional returns REQUESTED, which is a real result and not an
        // error — but it is not a booking, and `Confirmed` cannot say so.
        const { result } = booking.state;
        // An app-paid booking's outcome (booking-v1 1.20.0): only with the
        // payments gate open, where `interpret` can answer it at all. The gate
        // first, so a closed build folds this branch away.
        if (PAYMENTS_ENABLED && payScreensChunk && result.kind !== "confirmed" && result.kind !== "requested") {
          const kind = result.kind;
          return (
            <WithStepChunk chunk={payScreensChunk} fallback={<FlowShell><StepSkeleton testId="step-chunk-loading" /></FlowShell>} failed={<FlowShell><PayChunkFallback /></FlowShell>}>
              {({ PaidBookingOutcome }) => (
                <FlowShell>
                  <PaidBookingOutcome kind={kind} outcome={result.outcome} clientEmail={account?.email ?? details.email ?? ""} />
                </FlowShell>
              )}
            </WithStepChunk>
          );
        }
        return (
          <FlowShell>
            <BookingOutcome
              outcome={result.outcome}
              // Only these two reach here: the others need the open gate above.
              kind={result.kind as "confirmed" | "requested"}
              clientEmail={account?.email ?? details.email ?? ""}
            />
          </FlowShell>
        );
      }

      // AN APP-PAID BOOKING WAITING FOR ITS PAYMENT (booking-v1 1.20.0, the
      // payments gate open): the press has already left for Stripe (W-1);
      // this screen shows while the page goes, and its button is a new press.
      if (PAYMENTS_ENABLED && payScreensChunk && booking.state.status === "pay") {
        const { expiresAt } = booking.state.start;
        // The hold rule: a pay screen for an earlier attempt's time names it.
        const forAppointment = booking.state.otherTime ? booking.state.outcome.appointment : undefined;
        return (
          <WithStepChunk chunk={payScreensChunk} fallback={<FlowShell><StepSkeleton testId="step-chunk-loading" /></FlowShell>} failed={<FlowShell><PayChunkFallback /></FlowShell>}>
            {({ PayRedirect }) => (
              <FlowShell>
                <PayRedirect expiresAt={expiresAt} onContinue={booking.continueToPayment} forAppointment={forAppointment} />
              </FlowShell>
            )}
          </WithStepChunk>
        );
      }

      // THE SERVER ANSWERED AND WE CANNOT DESCRIBE THE ANSWER — a deposit
      // journey this cohort does not have, facts that contradict each other, or
      // an appointment that is not the one the client selected.
      //
      // Deliberately NOT folded into `failed`. That copy says "nothing has
      // changed", and here something may well have: a booking may exist. The
      // one thing this screen must not do is invite a second attempt.
      if (booking.state.status === "unauthorized") {
        return <FlowShell><BookingUnauthorized business={profile.businessName} /></FlowShell>;
      }

      // The five review states, derived from the hold the layout now owns.
      //
      // This is why the hold was hoisted: expiry and conflict happen on a clock
      // that keeps running while the client types their name, so the screen
      // that commits has to see them. `offline` is read from the browser
      // because it is the one state the server cannot tell us about.
      //
      // ORDER MATTERS, and it runs most-specific first. A commit failure
      // outranks the hold's own state: once the server has answered about the
      // booking, what the hold thinks is stale.
      //
      // The create-time failure branch used to be absent entirely. A commit
      // that came back SLOT_UNAVAILABLE fell through to "default" — no notice,
      // Confirm still live — so the client pressed a button that could never
      // succeed, on a slot someone else already owned, indefinitely.
      const commitFailure =
        booking.state.status === "failed" ? booking.state.failure : null;

      const reviewState = reviewStateFor({
        online: onlineNow,
        booking: booking.state.status,
        commitFailure,
        hold: hold.state,
        afterCheck: booking.state.status === "failed" && booking.state.afterCheck === true,
      });

      return (
        <FlowShell>
        <StepReview
          slug={slug}
          profile={profile}
          selection={flow.selection}
          client={{ name: bookingName, email: bookingEmail }}
          state={reviewState}
          secondsLeft={hold.state.status === "held" ? hold.state.secondsLeft : null}
          holdExpiresAt={hold.state.status === "held" ? hold.state.hold.expiresAt : null}
          sending={booking.state.status === "sending"}
          policyNote={policyNote}
          checking={booking.state.status === "uncertain" && booking.state.checking}
          checkAlsoLost={booking.state.status === "uncertain" && booking.state.checkAlsoLost}
          payWaiting={PAYMENTS_ENABLED && !CHECKOUT_EXPIRED_REPLAY_IS_DEFINITE && booking.state.status === "uncertain" && booking.state.payWaiting === true}
          onCheckResult={() => {
            // EXPLICIT ONLY. This closure is reachable from the button's
            // onClick and from nowhere else — no effect, no timer, no
            // visibility or reconnect handler calls it.
            //
            // NO ARGUMENTS. The hook holds the expectation the first send was
            // given, frozen alongside the request, so the replay is judged
            // against exactly what the original was judged against. Passing
            // live state here would re-derive it from a hold that has very
            // likely expired while the answer was lost.
            void booking.checkResult();
          }}
          // STRAIGHT THROUGH FROM THE PROFILE, null included. This used to be
          // `t("flow.review.policyPending")` — our own placeholder, because
          // nothing pre-booking carried the policy. `booking-v1@1.2.0` closes
          // that gap, and passing the server's value unchanged (rather than
          // coalescing null to any string of ours) is what keeps the
          // fail-closed case fail-closed.
          cancellationPolicyDisplay={profile.cancellationPolicyDisplay}
          terms={terms}
          onConfirm={() => {
            // NO SILENT BYPASS. An anonymous guest never reaches this Review
            // (the host renders theirs), and this refuses anyway: an
            // anonymous commit here would book without a verified proof.
            if (account === null) return;
            if (hold.state.status !== "held") return;
            // NO TERMS DECISION, NO SEND. Loading, unreadable, or published and
            // unticked: the button is disabled for all three, and this refuses
            // anyway: the handler must not depend on the button (Book.guards.test.tsx).
            const termsDecision = terms.decision();
            if (!termsDecision) return;
            // The step guard rails already redirect without a service, so this
            // is unreachable in practice — but `commit` compares this value to
            // what the server returns, and a comparison against a missing id
            // would silently pass nothing rather than check anything.
            const { serviceId } = flow.selection;
            if (!serviceId) return;
            // A SIGNED-IN CLIENT ONLY (the guard above). Backend takes the
            // contact from the account and refuses `guestContact` from a
            // signed-in caller, so none is sent.
            // What we believe we are booking, so the hook can refuse a result
            // that describes a different appointment than the one chosen.
            // A newer release live: commit itself sends nothing (rc.9 item 2).
            // The policy this Review shows, at the press: re-read just before
            // anything is sent (rc.10). Changed or unreadable stops the press.
            const shownPolicy = profile.cancellationPolicyDisplay;
            void booking.commit(
              hold.state.hold.id,
              {
                professionalId: profile.professionalId,
                serviceId,
                startTime: hold.state.hold.startTime,
              },
              termsDecision,
              async () => {
                const check = await checkPolicy(rereadPolicy, shownPolicy);
                setPolicyStop(check === "same" ? null : { check, scope: termsScope });
                return check === "same";
              },
              // The server's expiry, as received: what a later replay is judged against (rc.11 1c).
              hold.state.hold.expiresAt
            );
          }}
        />
        </FlowShell>
      );
    }
  }
  };

  return withGuestHost(renderStep());
}

// This tab's checkout, on a fresh load: its return page reads the status (the boot takes the ref from the tab's record), as PayRedirect does on a bfcache restore.
function FreshCheckoutReturn() {
  const navigate = useNavigate();
  useEffect(() => {
    bootCheckoutReturn();
    navigate(CHECKOUT_RETURN_PATH, { replace: true });
  }, [navigate]);
  return <FlowShell><StepSkeleton testId="step-chunk-loading" /></FlowShell>;
}

export default Book;
